import { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	ANALYTICS_EVENT,
	ANALYTICS_MONETIZATION_SOURCE,
} from '@/common/constants/analyticsEvents';
import { TOKEN_PACKAGE_PRICES } from '@/common/constants/tokenPackagePrices';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { useAnalytics } from '@/common/contexts/AnalyticsContext';
import BuyTokensModalHero from './components/BuyTokensModalHero';
import BuyTokensModalUsage from './components/BuyTokensModalUsage';
import BuyTokensPackageList from './components/BuyTokensPackageList';
import BuyTokensTermsCheckbox from './components/BuyTokensTermsCheckbox';
import BuyTokensCheckoutButton from './components/BuyTokensCheckoutButton';
import { useBuyTokensPayment } from './hooks/useBuyTokensPayment';
import {
	SparkCard,
	SparkClose,
	SparkContent,
	SparkHeader,
	SparkModal,
} from './BuyTokensModal.styles';

const BuyTokensModal = ({
	onClose,
	returnPath = '/chat',
	source = ANALYTICS_MONETIZATION_SOURCE.TOPBAR,
	title,
	description,
	heroTitle,
	heroDescription,
	headerTitle,
}) => {
	const { lang } = useContext(LanguageContext);
	const t = useLanguageData('sparkModal');
	const { user } = useGuestAuth();
	const { trackAnalytics } = useAnalytics();
	const [selectedKey, setSelectedKey] = useState(null);
	const [termsAccepted, setTermsAccepted] = useState(false);
	const packagePrices = TOKEN_PACKAGE_PRICES[lang] || TOKEN_PACKAGE_PRICES.en;
	const selectedPrice = selectedKey ? packagePrices[selectedKey] : null;

	const { canPurchase, handlePayment, isPending } = useBuyTokensPayment({
		returnPath,
		userId: user?.id,
		userEmail: user?.email,
		selectedKey,
		termsAccepted,
	});

	useEffect(() => {
		trackAnalytics(ANALYTICS_EVENT.TOKENS.MODAL_OPEN, {
			source,
			returnPath,
		});
	}, []);

	const handlePackageSelect = (packageKey) => {
		setSelectedKey(packageKey);
		trackAnalytics(ANALYTICS_EVENT.TOKENS.PACKAGE_SELECT, {
			packageKey,
			source,
		});
	};

	const handleCheckoutClick = () => {
		trackAnalytics(ANALYTICS_EVENT.TOKENS.CHECKOUT_CLICK, {
			packageKey: selectedKey,
			source,
		});
		handlePayment();
	};

	return createPortal(
		<SparkModal aria-hidden="false" onClick={onClose}>
			<SparkCard
				role="dialog"
				aria-modal="true"
				aria-labelledby="sparkTitle"
				onClick={(event) => event.stopPropagation()}
			>
				<BuyTokensModalHero
					heroTitle={heroTitle}
					heroDescription={heroDescription}
					fallbackTitle={t.premiumAccess}
					fallbackDescription={t.premiumDescription}
				/>
				<SparkContent>
					<SparkClose
						type="button"
						aria-label={t.closeSparkPackages}
						onClick={onClose}
					>
						×
					</SparkClose>
					<SparkHeader>
						<h2 id="sparkTitle">{headerTitle || title || t.selectPackage}</h2>
					</SparkHeader>
					<BuyTokensModalUsage description={description} translations={t} />
					<BuyTokensPackageList
						selectedKey={selectedKey}
						onSelect={handlePackageSelect}
						packagePrices={packagePrices}
						packagesLabel={t.sparkPackages}
						packageTranslations={t.packages}
					/>
					<BuyTokensTermsCheckbox
						checked={termsAccepted}
						onChange={(event) => setTermsAccepted(event.target.checked)}
						translations={t}
					/>
					<BuyTokensCheckoutButton
						canPurchase={canPurchase}
						isPending={isPending}
						selectedPrice={selectedPrice}
						onClick={handleCheckoutClick}
						translations={t}
					/>
				</SparkContent>
			</SparkCard>
		</SparkModal>,
		document.body,
	);
};

export default BuyTokensModal;
